import { GlobalStyles } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';

// Public embedding hooks only. Keep this shared by every workflow, including
// elements mounted in portals; never inject styles into a component's shadow root.
const elements = [
  'nicheaim-time-tracker',
  'nicheaim-recent-time-entries',
  'nicheaim-timesheets',
  'nicheaim-timesheet-administration',
];
const hosts = elements.join(', ');
const part = (name: string, state = '') =>
  elements.map((element) => `${element}::part(${name})${state}`).join(', ');

export const nicheAimTimeTrackingStyles = (theme: Theme) => {
  const { palette, typography, shape } = theme;
  const light = palette.mode === 'light';
  const radius = `${shape.borderRadius}px`;
  const shadows = theme.customShadows;
  return {
    [hosts]: {
      fontFamily: typography.fontFamily,
      fontSize: typography.body2.fontSize,
      lineHeight: typography.body2.lineHeight,
      '--nicheaim-color-scheme': palette.mode,
      '--nicheaim-font-family': typography.fontFamily,
      '--nicheaim-font-size': typography.body2.fontSize,
      '--nicheaim-line-height': typography.body2.lineHeight,
      '--nicheaim-heading-font-size': typography.h6.fontSize,
      '--nicheaim-subheading-font-size': typography.subtitle1.fontSize,
      '--nicheaim-small-font-size': typography.caption.fontSize,
      '--nicheaim-text-color': palette.text.primary,
      '--nicheaim-muted-text-color': palette.text.secondary,
      '--nicheaim-placeholder-color': palette.text.disabled,
      '--nicheaim-surface-color': palette.background.paper,
      '--nicheaim-muted-surface-color': palette.background.neutral || palette.background.default,
      '--nicheaim-hover-color': palette.action.hover,
      '--nicheaim-border-color': palette.divider,
      '--nicheaim-accent-color': palette.primary.main,
      '--nicheaim-accent-text-color': palette.primary.contrastText,
      '--nicheaim-accent-hover-color': palette.primary.dark,
      '--nicheaim-focus-color': palette.primary.main,
      '--nicheaim-danger-color': palette.error[light ? 'dark' : 'light'],
      '--nicheaim-notice-color': alpha(palette.info.main, 0.16),
      '--nicheaim-approved-color': palette.success[light ? 'dark' : 'light'],
      '--nicheaim-approved-background': alpha(palette.success.main, 0.16),
      '--nicheaim-approved-border': 'transparent',
      '--nicheaim-rejected-color': palette.error[light ? 'dark' : 'light'],
      '--nicheaim-rejected-background': alpha(palette.error.main, 0.16),
      '--nicheaim-rejected-border': 'transparent',
      '--nicheaim-border-radius': `${Number(shape.borderRadius) * 2}px`,
      '--nicheaim-control-radius': radius,
      '--nicheaim-checkbox-size': '24px',
      '--nicheaim-checkbox-radius': '4px',
      '--nicheaim-checkbox-border-width': '2px',
      '--nicheaim-checkbox-border-color': palette.text.secondary,
      '--nicheaim-checkbox-background': palette.background.paper,
      '--nicheaim-checkbox-checked-color': palette.primary.main,
      '--nicheaim-checkbox-mark-color': palette.primary.contrastText,
      '--nicheaim-checkbox-disabled-color': palette.action.disabled,
      '--nicheaim-checkbox-hover-color': alpha(palette.primary.main, palette.action.hoverOpacity),
      '--nicheaim-switch-track-color': palette.grey[500],
      '--nicheaim-switch-thumb-color': palette.grey[light ? 100 : 300],
      '--nicheaim-switch-checked-track-color': alpha(palette.primary.main, 0.48),
      '--nicheaim-switch-checked-thumb-color': palette.primary.main,
      '--nicheaim-switch-thumb-shadow': shadows?.z1 || theme.shadows[1],
      '--nicheaim-filter-width': '320px',
      '--nicheaim-filter-label-font-size': typography.subtitle1.fontSize,
      '--nicheaim-filter-label-font-weight': typography.fontWeightBold,
      '--nicheaim-filter-control-height': '56px',
      '--nicheaim-filter-action-height': '48px',
      '--nicheaim-chip-background': palette.action.selected,
      '--nicheaim-chip-color': palette.text.primary,
      '--nicheaim-chip-radius': radius,
      '--nicheaim-button-radius': radius,
      '--nicheaim-button-font-weight': typography.button.fontWeight,
      '--nicheaim-button-text-transform': typography.button.textTransform,
      '--nicheaim-button-letter-spacing': typography.button.letterSpacing || 'normal',
      '--nicheaim-spacing': theme.spacing(3),
      '--nicheaim-gap': theme.spacing(1),
      '--nicheaim-control-padding': theme.spacing(1, 1.5),
      '--nicheaim-cell-padding': theme.spacing(2),
      '--nicheaim-disabled-opacity': palette.action.disabledOpacity,
      '--nicheaim-panel-shadow': shadows?.card || theme.shadows[1],
      '--nicheaim-button-shadow': 'none',
      '--nicheaim-overlay-shadow': shadows?.dropdown || theme.shadows[8],
      '--nicheaim-backdrop-color': `linear-gradient(75deg, ${alpha(palette.grey[900], 0.48)} 0%, ${
        palette.grey[900]
      } 100%)`,
      '--nicheaim-overlay-z-index': theme.zIndex.modal,
    },
    [part('button')]: {
      fontSize: typography.button.fontSize,
      lineHeight: typography.button.lineHeight,
    },
    [part('primary-button')]: {
      boxShadow: shadows?.primary || theme.shadows[2],
    },
    [part('button', ':hover')]: { boxShadow: 'none' },
    // Outlined fields follow Careflow's MuiOutlinedInput override.
    [`${part('input')}, ${part('select')}, ${part('textarea')}`]: {
      borderColor: alpha(palette.grey[500], 0.32),
    },
    [`${part('input', ':focus')}, ${part('select', ':focus')}, ${part('textarea', ':focus')}`]: {
      borderColor: palette.primary.main,
    },
    [part('checkbox')]: { borderColor: palette.text.secondary },
    [part('checkbox', ':checked')]: { borderColor: palette.primary.main },
    [part('checkbox', ':indeterminate')]: { borderColor: palette.primary.main },
    [part('checkbox', ':disabled')]: { borderColor: palette.action.disabled },
    [part('heading')]: { fontWeight: typography.h6.fontWeight },
    'nicheaim-timesheet-administration::part(authorization-panel)': {
      border: 0,
      boxShadow: 'none',
      padding: 0,
      display: 'grid',
      gap: theme.spacing(2),
      justifyItems: 'start',
      minWidth: 0,
      overflowWrap: 'anywhere' as const,
    },
    'nicheaim-timesheet-administration::part(authorization-list)': {
      width: '100%',
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'grid',
      gap: theme.spacing(1.5),
      minWidth: 0,
    },
    'nicheaim-timesheet-administration::part(authorization-row)': {
      padding: theme.spacing(2),
      border: `1px solid ${palette.divider}`,
      borderRadius: radius,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      gap: theme.spacing(1),
      minWidth: 0,
      overflowWrap: 'anywhere' as const,
    },
    'nicheaim-timesheet-administration::part(authorization-effective-permissions)': {
      flexBasis: '100%',
      minWidth: 0,
      color: palette.text.secondary,
      overflowWrap: 'anywhere' as const,
    },
    'nicheaim-timesheet-administration::part(role-editor)': {
      width: '100%',
      display: 'grid',
      gap: theme.spacing(2),
      minWidth: 0,
    },

    'nicheaim-timesheet-administration::part(heading)': { marginBlock: 0 },
    'nicheaim-timesheet-administration::part(hint)': { marginBlock: 0 },
    'nicheaim-timesheet-administration::part(checkbox)': {
      marginInlineEnd: theme.spacing(1),
      marginBlock: theme.spacing(0.5),
      verticalAlign: 'middle',
      flexShrink: 0,
    },
    [part('label')]: { fontWeight: typography.fontWeightMedium },
    [part('dialog')]: {
      border: 0,
      boxShadow: shadows?.dialog || theme.shadows[24],
    },
    [`${part('menu')}, ${part('column-panel')}, ${part('sort-panel')}`]: {
      borderRadius: radius,
      boxShadow: shadows?.dropdown || theme.shadows[8],
    },
    [part('filter-drawer')]: { borderRadius: 0, maxWidth: '100vw' },
    [part('filter-popover')]: {
      borderRadius: radius,
      boxShadow: shadows?.dropdown || theme.shadows[8],
    },
    [part('filter-header')]: { padding: theme.spacing(1, 2.8) },
    [part('combobox')]: { fontSize: typography.body1.fontSize },
    [part('option')]: {
      fontSize: typography.body1.fontSize,
      fontWeight: typography.fontWeightRegular,
    },
    [part('filter-close')]: {
      border: 0,
      background: 'transparent',
      color: palette.text.secondary,
      fontSize: '24px',
      lineHeight: 1,
      padding: 0,
      width: 44,
      height: 44,
      minWidth: 44,
      minHeight: 44,
    },
    [part('filter-chip')]: { boxShadow: 'none', textTransform: 'none' as const },
    [part('tooltip')]: {
      color: palette.common.white,
      backgroundColor: palette.grey[light ? 800 : 700],
      fontSize: typography.caption.fontSize,
      border: 0,
    },
    [part('table-scroll')]: { border: 0 },
    [part('column-header')]: {
      backgroundColor: palette.background.neutral || palette.background.default,
      color: palette.text.secondary,
      fontSize: typography.body2.fontSize,
      fontWeight: typography.fontWeightMedium,
      borderBottom: 0,
    },
    [part('cell')]: { borderBottom: 0 },
    // Keep dates and words intact while the document table scrolls on narrow pages.
    'nicheaim-timesheets::part(cell)': { overflowWrap: 'break-word' as const },
    [part('sort-button')]: { fontWeight: typography.fontWeightMedium },
    [part('pagination')]: {
      borderTop: `1px solid ${palette.divider}`,
      paddingTop: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    // Match Careflow Label's compact ghost status treatment.
    [part('approval-badge')]: {
      borderRadius: '6px',
      fontSize: typography.caption.fontSize,
      fontWeight: typography.fontWeightBold,
      textTransform: 'uppercase' as const,
    },
    [theme.breakpoints.down('sm')]: {
      [hosts]: {
        '--nicheaim-spacing': theme.spacing(2),
        '--nicheaim-cell-padding': theme.spacing(1),
      },
    },
    '@media (forced-colors: active)': {
      [part('approval-badge')]: { border: '1px solid CanvasText' },
    },
  };
};

export default function NicheAimTimeTrackingStyles() {
  return <GlobalStyles styles={nicheAimTimeTrackingStyles} />;
}
